import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Upload as UploadIcon, 
  FileText, 
  X, 
  CheckCircle, 
  AlertCircle, 
  Info,
  Download,
  Eye,
  Trash2
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { uploadFile } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';

const Upload = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  // State
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [showPreview, setShowPreview] = useState(false);

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: uploadFile,
    onSuccess: (data) => {
      toast.success('File uploaded successfully!');
      queryClient.invalidateQueries({ queryKey: ['batches'] });
      queryClient.invalidateQueries({ queryKey: ['chargebacks'] });
      
      // Navigate to the created batch
      if (data.batchId) {
        navigate(`/batches/${data.batchId}`);
      } else {
        navigate('/batches');
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to upload file');
      setUploading(false);
    }
  });

  // Dropzone configuration
  const onDrop = useCallback((acceptedFiles) => {
    const newFiles = acceptedFiles.map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      status: 'pending',
      progress: 0
    }));
    
    setFiles(prev => [...prev, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/plain': ['.txt'],
      'application/edi-x12': ['.edi', '.844', '.845', '.849']
    },
    multiple: true,
    maxSize: 50 * 1024 * 1024 // 50MB
  });

  // Remove file
  const removeFile = useCallback((fileId) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  }, []);

  // Preview file
  const previewFile = useCallback((file) => {
    if (file.type.includes('text') || file.type.includes('csv')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target.result;
        const lines = content.split('\n').slice(0, 10); // First 10 lines
        alert(`File Preview (first 10 lines):\n\n${lines.join('\n')}`);
      };
      reader.readAsText(file);
    } else {
      toast.info('Preview not available for this file type');
    }
  }, []);

  // Handle upload
  const handleUpload = useCallback(async () => {
    if (files.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    setUploading(true);
    
    try {
      for (const fileObj of files) {
        setUploadProgress(prev => ({
          ...prev,
          [fileObj.id]: 0
        }));

        const formData = new FormData();
        formData.append('file', fileObj.file);
        formData.append('fileType', getFileType(fileObj.file));
        formData.append('description', `Uploaded by ${user?.email}`);

        // Simulate progress
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => ({
            ...prev,
            [fileObj.id]: Math.min(prev[fileObj.id] + Math.random() * 20, 90)
          }));
        }, 200);

        await uploadMutation.mutateAsync(formData);
        
        clearInterval(progressInterval);
        setUploadProgress(prev => ({
          ...prev,
          [fileObj.id]: 100
        }));

        // Update file status
        setFiles(prev => prev.map(f => 
          f.id === fileObj.id ? { ...f, status: 'completed' } : f
        ));
      }
      
      toast.success('All files uploaded successfully!');
      setTimeout(() => {
        navigate('/batches');
      }, 2000);
      
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  }, [files, uploadMutation, user, navigate]);

  // Get file type
  const getFileType = (file) => {
    if (file.name.endsWith('.csv')) return 'CSV';
    if (file.name.endsWith('.xls') || file.name.endsWith('.xlsx')) return 'EXCEL';
    if (file.name.endsWith('.edi') || file.name.endsWith('.844') || file.name.endsWith('.845') || file.name.endsWith('.849')) return 'EDI';
    if (file.name.endsWith('.txt')) return 'TEXT';
    return 'UNKNOWN';
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Get file icon
  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'CSV':
      case 'EXCEL':
        return <FileText className="h-8 w-8 text-blue-500" />;
      case 'EDI':
        return <FileText className="h-8 w-8 text-green-500" />;
      case 'TEXT':
        return <FileText className="h-8 w-8 text-gray-500" />;
      default:
        return <FileText className="h-8 w-8 text-gray-400" />;
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">File Upload</h1>
        <p className="text-gray-600">
          Upload EDI files, Excel spreadsheets, or CSV files for chargeback processing
        </p>
      </div>

      {/* Upload Zone */}
      <div className="mb-8">
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
            isDragActive
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
          }`}
        >
          <input {...getInputProps()} />
          <UploadIcon className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {isDragActive ? 'Drop files here' : 'Drag & drop files here'}
          </h3>
          <p className="text-gray-500 mb-4">
            or click to select files
          </p>
          <div className="text-sm text-gray-400">
            <p>Supported formats: CSV, Excel (.xls, .xlsx), EDI (.edi, .844, .845, .849), Text (.txt)</p>
            <p>Maximum file size: 50MB</p>
          </div>
        </div>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Files to Upload ({files.length})
              </h3>
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="text-sm text-primary-600 hover:text-primary-700"
                title={showPreview ? 'Hide Preview' : 'Show Preview'}
              >
                {showPreview ? 'Hide' : 'Show'} Preview
              </button>
            </div>
          </div>
          
          <div className="divide-y divide-gray-200">
            {files.map((fileObj) => (
              <div key={fileObj.id} className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {getFileIcon(getFileType(fileObj.file))}
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">
                        {fileObj.file.name}
                      </h4>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>{formatFileSize(fileObj.file.size)}</span>
                        <span>{getFileType(fileObj.file)}</span>
                        <span>Uploaded: {new Date().toLocaleTimeString()}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => previewFile(fileObj.file)}
                      className="p-2 text-gray-400 hover:text-gray-600"
                      title="Preview"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      onClick={() => removeFile(fileObj.id)}
                      className="p-2 text-red-400 hover:text-red-600"
                      title="Remove"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                
                {/* Progress Bar */}
                {uploading && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                      <span>
                        {fileObj.status === 'completed' ? 'Completed' : 'Uploading...'}
                      </span>
                      <span>{Math.round(uploadProgress[fileObj.id] || 0)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress[fileObj.id] || 0}%` }}
                      />
                    </div>
                  </div>
                )}
                
                {/* Status */}
                {fileObj.status === 'completed' && (
                  <div className="mt-4 flex items-center text-green-600">
                    <CheckCircle size={16} className="mr-2" />
                    <span className="text-sm font-medium">Upload completed</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Button */}
      {files.length > 0 && (
        <div className="flex justify-center">
          <button
            onClick={handleUpload}
            disabled={uploading}
            className="bg-primary-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            title="Upload files"
          >
            {uploading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Uploading...</span>
              </>
            ) : (
              <>
                <UploadIcon size={20} />
                <span>Upload {files.length} File{files.length !== 1 ? 's' : ''}</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Information Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Info className="h-8 w-8 text-blue-600 mr-3" />
            <h3 className="text-lg font-semibold text-blue-900">EDI Files</h3>
          </div>
          <p className="text-blue-700 text-sm">
            Upload EDI 844 (chargeback requests), 845 (price notifications), and 849 (reconciliation) files for automated processing.
          </p>
        </div>
        
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <FileText className="h-8 w-8 text-green-600 mr-3" />
            <h3 className="text-lg font-semibold text-green-900">Excel & CSV</h3>
          </div>
          <p className="text-green-700 text-sm">
            Import chargeback data from Excel spreadsheets or CSV files. The system will automatically map columns and validate data.
          </p>
        </div>
        
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Download className="h-8 w-8 text-purple-600 mr-3" />
            <h3 className="text-lg font-semibold text-purple-900">Batch Processing</h3>
          </div>
          <p className="text-purple-700 text-sm">
            Files are processed in batches with validation, error checking, and detailed reporting. Monitor progress in the Batches section.
          </p>
        </div>
      </div>

      {/* File Format Requirements */}
      <div className="mt-12 bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">File Format Requirements</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-2">EDI Files (.edi, .844, .845, .849)</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Must follow X12 EDI standards</li>
              <li>• Include proper segment terminators</li>
              <li>• Contain required data elements</li>
              <li>• Maximum 50MB file size</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Excel/CSV Files</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• First row should contain headers</li>
              <li>• Include customer ID, product SKU, quantity, price</li>
              <li>• Date format: MM/DD/YYYY or YYYY-MM-DD</li>
              <li>• Currency values without symbols</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Upload;
